/**
 * YouTube video preview + modal player for the YouTube Video section.
 *
 * Sends Google Tag Manager dataLayer events:
 * - youtube_video_click
 * - youtube_video_play
 * - youtube_video_stop
 */

(function () {
  'use strict';

  var YT_API_SRC = 'https://www.youtube.com/iframe_api';
  var ytApiPromise = null;

  function loadYouTubeApi() {
    if (window.YT && window.YT.Player) {
      return Promise.resolve(window.YT);
    }

    if (ytApiPromise) {
      return ytApiPromise;
    }

    ytApiPromise = new Promise(function (resolve) {
      var previousReady = window.onYouTubeIframeAPIReady;

      window.onYouTubeIframeAPIReady = function () {
        if (typeof previousReady === 'function') {
          previousReady();
        }
        resolve(window.YT);
      };

      if (!document.querySelector('script[src="' + YT_API_SRC + '"]')) {
        var tag = document.createElement('script');
        tag.src = YT_API_SRC;
        document.head.appendChild(tag);
      }
    });

    return ytApiPromise;
  }

  function pushAnalyticsEvent(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      Object.assign(
        {
          event: eventName,
        },
        payload
      )
    );
  }

  function formatSeconds(value) {
    var seconds = Math.max(0, Math.round(Number(value) || 0));
    var minutes = Math.floor(seconds / 60);
    var remainder = seconds % 60;
    return minutes + ':' + String(remainder).padStart(2, '0');
  }

  function YouTubeVideoSection(section) {
    this.section = section;
    this.videoId = section.dataset.videoId || '';
    this.videoTitle = section.dataset.videoTitle || 'YouTube video';
    this.sectionId = section.dataset.sectionId || '';
    this.trigger = section.querySelector('[data-youtube-play-trigger]');
    this.modal = section.querySelector('.youtube-video-modal');
    this.overlay = section.querySelector('.youtube-video-modal__overlay');
    this.closeButton = section.querySelector('.youtube-video-modal__close');
    this.playerContainer = section.querySelector('[data-youtube-player]');
    this.player = null;
    this.playTracked = false;
    this.lastFocus = null;

    if (!this.videoId || !this.trigger || !this.modal || !this.playerContainer) {
      return;
    }

    this.bindEvents();
  }

  YouTubeVideoSection.prototype.getAnalyticsPayload = function () {
    return {
      video_id: this.videoId,
      video_title: this.videoTitle,
      section_id: this.sectionId,
    };
  };

  YouTubeVideoSection.prototype.bindEvents = function () {
    var self = this;

    this.trigger.addEventListener('click', function (event) {
      event.preventDefault();
      self.open();
    });

    this.closeButton.addEventListener('click', function (event) {
      event.preventDefault();
      self.close('close_button');
    });

    this.overlay.addEventListener('click', function () {
      self.close('overlay');
    });

    this.modal.addEventListener('click', function (event) {
      if (event.target === self.modal) {
        self.close('overlay');
      }
    });

    this.onKeyDown = function (event) {
      if (event.key === 'Escape' && self.modal.classList.contains('is-open')) {
        self.close('escape_key');
      }
    };

    document.addEventListener('keydown', this.onKeyDown);
  };

  YouTubeVideoSection.prototype.open = function () {
    var self = this;

    pushAnalyticsEvent('youtube_video_click', this.getAnalyticsPayload());

    this.lastFocus = document.activeElement;
    this.playTracked = false;
    this.modal.hidden = false;
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    loadYouTubeApi().then(function (YT) {
      if (!self.modal.classList.contains('is-open')) {
        return;
      }

      self.createPlayer(YT);
      self.closeButton.focus();
    });
  };

  YouTubeVideoSection.prototype.createPlayer = function (YT) {
    var self = this;

    this.destroyPlayer();
    this.playerContainer.innerHTML = '';

    this.player = new YT.Player(this.playerContainer, {
      videoId: this.videoId,
      playerVars: {
        autoplay: 1,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        origin: window.location.origin,
      },
      events: {
        onStateChange: function (event) {
          self.onPlayerStateChange(event);
        },
      },
    });
  };

  YouTubeVideoSection.prototype.onPlayerStateChange = function (event) {
    if (this.playTracked || !this.player || event.data !== 1) {
      return;
    }

    this.playTracked = true;

    pushAnalyticsEvent(
      'youtube_video_play',
      Object.assign({}, this.getAnalyticsPayload(), {
        video_duration_seconds: this.getDuration(),
      })
    );
  };

  YouTubeVideoSection.prototype.getCurrentTime = function () {
    if (!this.player || typeof this.player.getCurrentTime !== 'function') {
      return 0;
    }

    return this.player.getCurrentTime();
  };

  YouTubeVideoSection.prototype.getDuration = function () {
    if (!this.player || typeof this.player.getDuration !== 'function') {
      return 0;
    }

    return this.player.getDuration();
  };

  YouTubeVideoSection.prototype.close = function (closeMethod) {
    var stopPosition = this.getCurrentTime();
    var duration = this.getDuration();
    var percentWatched = duration > 0 ? Math.round((stopPosition / duration) * 100) : 0;

    pushAnalyticsEvent(
      'youtube_video_stop',
      Object.assign({}, this.getAnalyticsPayload(), {
        close_method: closeMethod || 'unknown',
        stop_position_seconds: Math.round(stopPosition),
        stop_position_formatted: formatSeconds(stopPosition),
        video_duration_seconds: Math.round(duration),
        video_percent_watched: percentWatched,
      })
    );

    this.destroyPlayer();
    this.modal.classList.remove('is-open');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    var self = this;
    window.setTimeout(function () {
      if (!self.modal.classList.contains('is-open')) {
        self.modal.hidden = true;
      }
    }, 200);

    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
    }
  };

  YouTubeVideoSection.prototype.destroyPlayer = function () {
    if (this.player && typeof this.player.destroy === 'function') {
      this.player.destroy();
    }

    this.player = null;

    if (this.playerContainer) {
      this.playerContainer.innerHTML = '';
    }
  };

  YouTubeVideoSection.prototype.destroy = function () {
    this.destroyPlayer();
    document.removeEventListener('keydown', this.onKeyDown);
  };

  function initYouTubeVideoSection(section) {
    if (!section || section.dataset.youtubeVideoReady === 'true') {
      return;
    }

    section.dataset.youtubeVideoReady = 'true';
    section._youtubeVideoInstance = new YouTubeVideoSection(section);
  }

  function initAllYouTubeVideoSections(root) {
    (root || document).querySelectorAll('.youtube-video-section[data-video-id]').forEach(initYouTubeVideoSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllYouTubeVideoSections(document);
    });
  } else {
    initAllYouTubeVideoSections(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAllYouTubeVideoSections(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('.youtube-video-section').forEach(function (section) {
      if (section._youtubeVideoInstance) {
        section._youtubeVideoInstance.destroy();
        delete section._youtubeVideoInstance;
      }
      delete section.dataset.youtubeVideoReady;
    });
  });
})();
